import { useRef, useCallback, useEffect, useState } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label: string;
}

export default function RichTextEditor({ value, onChange, label }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const savedSelection = useRef<Range | null>(null);

  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const execCommand = useCallback((command: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    handleInput();
  }, [handleInput]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertParagraph', false);
      handleInput();
    }
  }, [handleInput]);

  const handleFontSize = useCallback((size: string) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed) return;

    // Remove any existing font-size spans in the selection first
    document.execCommand('removeFormat', false);

    const span = document.createElement('span');
    span.style.fontSize = size;

    try {
      range.surroundContents(span);
      handleInput();
    } catch {
      document.execCommand('fontSize', false, '7');
      const fontElements = editorRef.current?.querySelectorAll('font[size="7"]');
      fontElements?.forEach(el => {
        const newSpan = document.createElement('span');
        newSpan.style.fontSize = size;
        newSpan.innerHTML = el.innerHTML;
        el.parentNode?.replaceChild(newSpan, el);
      });
      handleInput();
    }
  }, [handleInput]);

  const handleBlock = useCallback((tag: string) => {
    execCommand('formatBlock', tag);
  }, [execCommand]);

  const saveSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelection.current = selection.getRangeAt(0).cloneRange();
      const selectedText = selection.toString();
      setLinkText(selectedText);
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (savedSelection.current) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelection.current);
      }
    }
  }, []);

  const handleInsertLink = useCallback(() => {
    if (!linkUrl.trim()) return;

    restoreSelection();

    let url = linkUrl.trim();
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      if (range.collapsed && linkText.trim()) {
        // No selection — insert new link with custom text
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.textContent = linkText.trim();
        anchor.style.color = '#8B4513';
        anchor.style.textDecoration = 'underline';
        range.insertNode(anchor);

        // Move cursor after the link
        range.setStartAfter(anchor);
        range.setEndAfter(anchor);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // Text is selected — wrap it in a link
        document.execCommand('createLink', false, url);

        // Style the newly created links
        const links = editorRef.current?.querySelectorAll(`a[href="${url}"]`);
        links?.forEach(link => {
          (link as HTMLAnchorElement).target = '_blank';
          (link as HTMLAnchorElement).rel = 'noopener noreferrer';
          (link as HTMLAnchorElement).style.color = '#8B4513';
          (link as HTMLAnchorElement).style.textDecoration = 'underline';
        });
      }
    }

    handleInput();
    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
    savedSelection.current = null;
  }, [linkUrl, linkText, restoreSelection, handleInput]);

  const handleRemoveLink = useCallback(() => {
    editorRef.current?.focus();
    document.execCommand('unlink', false);
    handleInput();
  }, [handleInput]);

  const openLinkModal = useCallback(() => {
    saveSelection();

    // Check if cursor is inside an existing link
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const anchor = selection.anchorNode?.parentElement?.closest('a');
      if (anchor) {
        setLinkUrl(anchor.href);
        setLinkText(anchor.textContent || '');
      } else {
        setLinkUrl('');
      }
    }

    setShowLinkModal(true);
  }, [saveSelection]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/html') || e.clipboardData.getData('text/plain');

    if (e.clipboardData.getData('text/html')) {
      // Clean up pasted HTML — remove unwanted styles but keep structure
      const temp = document.createElement('div');
      temp.innerHTML = text;

      // Remove background colors and font-family from pasted content
      temp.querySelectorAll('*').forEach(el => {
        const htmlEl = el as HTMLElement;
        if (htmlEl.style) {
          htmlEl.style.backgroundColor = '';
          htmlEl.style.background = '';
          htmlEl.style.fontFamily = '';
        }
      });

      document.execCommand('insertHTML', false, temp.innerHTML);
    } else {
      // Plain text — insert with line breaks preserved
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '<')
        .replace(/>/g, '>')
        .replace(/\n/g, '<br>');
      document.execCommand('insertHTML', false, escaped);
    }

    handleInput();
  }, [handleInput]);

  const fontSizes = [
    { label: 'Pequeno', value: '12px' },
    { label: 'Normal', value: '14px' },
    { label: 'Médio', value: '16px' },
    { label: 'Grande', value: '18px' },
    { label: 'Maior', value: '20px' },
    { label: 'Extra', value: '24px' }
  ];

  const blockFormats = [
    { label: 'Parágrafo', value: 'p' },
    { label: 'Título 1', value: 'h1' },
    { label: 'Título 2', value: 'h2' },
    { label: 'Título 3', value: 'h3' }
  ];

  const btnClass = "px-2 py-1.5 rounded text-sm font-medium border border-gray-300 hover:bg-gray-100 active:bg-gray-200 transition-colors duration-150 min-w-[32px]";

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>

      <div className="border border-gray-300 rounded-lg overflow-hidden">
        {/* Toolbar */}
        <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-1 items-center">
          {/* Text Formatting */}
          <div className="flex gap-1 items-center border-r border-gray-300 pr-2 mr-1">
            <button
              type="button"
              onClick={() => execCommand('bold')}
              className={btnClass}
              title="Negrito (Ctrl+B)"
              style={{ fontWeight: 'bold' }}
            >
              B
            </button>
            <button
              type="button"
              onClick={() => execCommand('italic')}
              className={btnClass}
              title="Itálico (Ctrl+I)"
              style={{ fontStyle: 'italic' }}
            >
              I
            </button>
            <button
              type="button"
              onClick={() => execCommand('underline')}
              className={btnClass}
              title="Sublinhado (Ctrl+U)"
              style={{ textDecoration: 'underline' }}
            >
              U
            </button>
          </div>

          {/* Font Size */}
          <div className="flex gap-1 items-center border-r border-gray-300 pr-2 mr-1">
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleFontSize(e.target.value);
                  e.target.value = '';
                }
              }}
              className="px-2 py-1.5 rounded text-sm border border-gray-300 bg-white hover:bg-gray-50 cursor-pointer"
              defaultValue=""
              title="Tamanho da fonte"
            >
              <option value="" disabled>Tamanho</option>
              {fontSizes.map(s => (
                <option key={s.value} value={s.value}>{s.label} ({s.value})</option>
              ))}
            </select>
          </div>

          {/* Alignment */}
          <div className="flex gap-1 items-center border-r border-gray-300 pr-2 mr-1">
            <button
              type="button"
              onClick={() => execCommand('justifyLeft')}
              className={btnClass}
              title="Alinhar à esquerda"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/>
              </svg>
            </button>
            <button
              type="button"
              onClick={() => execCommand('justifyCenter')}
              className={btnClass}
              title="Centrar"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/>
              </svg>
            </button>
            <button
              type="button"
              onClick={() => execCommand('justifyFull')}
              className={btnClass}
              title="Justificar"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Lists */}
          <div className="flex gap-1 items-center border-r border-gray-300 pr-2 mr-1">
            <button
              type="button"
              onClick={() => execCommand('insertUnorderedList')}
              className={btnClass}
              title="Lista com marcadores"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="9" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="9" y1="18" x2="21" y2="18"/>
                <circle cx="4" cy="6" r="1.5" fill="currentColor"/><circle cx="4" cy="12" r="1.5" fill="currentColor"/><circle cx="4" cy="18" r="1.5" fill="currentColor"/>
              </svg>
            </button>
            <button
              type="button"
              onClick={() => execCommand('insertOrderedList')}
              className={btnClass}
              title="Lista numerada"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/>
                <text x="3" y="8" fontSize="8" fill="currentColor" fontFamily="sans-serif">1</text>
                <text x="3" y="14" fontSize="8" fill="currentColor" fontFamily="sans-serif">2</text>
                <text x="3" y="20" fontSize="8" fill="currentColor" fontFamily="sans-serif">3</text>
              </svg>
            </button>
          </div>

          {/* Hyperlink */}
          <div className="flex gap-1 items-center border-r border-gray-300 pr-2 mr-1">
            <button
              type="button"
              onClick={openLinkModal}
              className={btnClass}
              title="Inserir link"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </button>
            <button
              type="button"
              onClick={handleRemoveLink}
              className={btnClass}
              title="Remover link"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          </div>

          {/* Block Format */}
          <div className="flex gap-1 items-center">
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleBlock(e.target.value);
                  e.target.value = '';
                }
              }}
              className="px-2 py-1.5 rounded text-sm border border-gray-300 bg-white hover:bg-gray-50 cursor-pointer"
              defaultValue=""
              title="Formato de bloco"
            >
              <option value="" disabled>Bloco</option>
              {blockFormats.map(b => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Editor Area */}
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className="p-4 min-h-[300px] max-h-[500px] overflow-y-auto focus:outline-none"
          style={{
            fontSize: '14px',
            lineHeight: '1.7',
            color: '#333',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}
          suppressContentEditableWarning
        />
      </div>

      <p className="text-xs text-gray-500">
        💡 Selecione texto para aplicar formatação. Use o botão 🔗 para inserir links clicáveis.
      </p>

      {/* Link Modal */}
      {showLinkModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowLinkModal(false);
              setLinkUrl('');
              setLinkText('');
              savedSelection.current = null;
            }
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4"
            style={{ animation: 'modalFadeIn 0.2s ease-out' }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8B4513" strokeWidth="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
              Inserir Hyperlink
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Texto do link
                </label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="Texto visível (ex: Clique aqui)"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Se tiver texto selecionado no editor, este campo é ignorado.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL do link
                </label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="https://exemplo.com"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleInsertLink();
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={handleInsertLink}
                disabled={!linkUrl.trim()}
                className="flex-1 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-2.5 px-4 rounded-lg transition duration-200 text-sm"
              >
                Inserir Link
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkUrl('');
                  setLinkText('');
                  savedSelection.current = null;
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2.5 px-4 rounded-lg transition duration-200 text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
